type Point = [x: number, y: number];
type Cubic = [c1: Point, c2: Point, end: Point];
export type Curve = { start: Point; cubics: Cubic[] };
type Row = { center: number; radius: number };

const HALF_PI = Math.PI / 2;
const TAU = Math.PI * 2;
const SPLITS_PER_CUBIC = 4;
const SAMPLES_PER_CUBIC = 80;

/** Only the `M`/`C`/`Z` absolute commands the illustration's export uses. */
export function parsePath(d: string): Curve {
	const numbers = (d.match(/-?\d*\.?\d+(?:e-?\d+)?/gi) ?? []).map(Number);
	const point = (index: number): Point => [numbers[index] ?? 0, numbers[index + 1] ?? 0];
	const cubics: Cubic[] = [];
	for (let index = 2; index + 5 < numbers.length; index += 6) {
		cubics.push([point(index), point(index + 2), point(index + 4)]);
	}
	return { start: point(0), cubics };
}

function lerp(a: Point, b: Point, t: number): Point {
	return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** De Casteljau at t, returning the two halves. */
function split(from: Point, cubic: Cubic, t: number): [Cubic, Cubic] {
	const [c1, c2, end] = cubic;
	const a = lerp(from, c1, t);
	const b = lerp(c1, c2, t);
	const c = lerp(c2, end, t);
	const ab = lerp(a, b, t);
	const bc = lerp(b, c, t);
	const mid = lerp(ab, bc, t);
	return [
		[a, ab, mid],
		[bc, c, end],
	];
}

/** Finer cubics bend closer to the true sphere once their control points are projected one by one. */
function subdivide(curve: Curve): Curve {
	const cubics: Cubic[] = [];
	let from = curve.start;
	for (const cubic of curve.cubics) {
		let rest = cubic;
		for (let piece = SPLITS_PER_CUBIC; piece > 1; piece--) {
			const [head, tail] = split(from, rest, 1 / piece);
			cubics.push(head);
			from = head[2];
			rest = tail;
		}
		cubics.push(rest);
		from = rest[2];
	}
	return { start: curve.start, cubics };
}

function sample(from: Point, cubic: Cubic, t: number): Point {
	const [c1, c2, end] = cubic;
	const u = 1 - t;
	const x = u ** 3 * from[0] + 3 * u * u * t * c1[0] + 3 * u * t * t * c2[0] + t ** 3 * end[0];
	const y = u ** 3 * from[1] + 3 * u * u * t * c1[1] + 3 * u * t * t * c2[1] + t ** 3 * end[1];
	return [x, y];
}

/** The hand-drawn outline is no circle, so each pixel row gets its own centre and half-width. */
export function measureRows(outline: Curve): (y: number) => Row {
	const points: Point[] = [outline.start];
	let from = outline.start;
	for (const cubic of outline.cubics) {
		for (let step = 1; step <= SAMPLES_PER_CUBIC; step++) points.push(sample(from, cubic, step / SAMPLES_PER_CUBIC));
		from = cubic[2];
	}
	points.push(outline.start);
	const top = Math.ceil(Math.min(...points.map((p) => p[1])));
	const bottom = Math.floor(Math.max(...points.map((p) => p[1])));
	const rows: Row[] = [];
	for (let y = top; y <= bottom; y++) {
		let left = Infinity;
		let right = -Infinity;
		for (let index = 1; index < points.length; index++) {
			const a = points[index - 1] ?? [0, 0];
			const b = points[index] ?? [0, 0];
			if ((a[1] - y) * (b[1] - y) > 0 || a[1] === b[1]) continue;
			const x = a[0] + ((y - a[1]) / (b[1] - a[1])) * (b[0] - a[0]);
			left = Math.min(left, x);
			right = Math.max(right, x);
		}
		rows.push({ center: (left + right) / 2, radius: Math.max((right - left) / 2, 0.5) });
	}
	return (y) => {
		const at = Math.min(Math.max(y - top, 0), rows.length - 1);
		const low = rows[Math.floor(at)] ?? { center: 0, radius: 1 };
		const high = rows[Math.ceil(at)] ?? low;
		const t = at - Math.floor(at);
		return { center: low.center + (high.center - low.center) * t, radius: low.radius + (high.radius - low.radius) * t };
	};
}

/** Past the limb the mapping keeps going linearly outward, so hidden content lands outside the clip. */
export function toLongitude(rows: (y: number) => Row, x: number, y: number) {
	const row = rows(y);
	const u = (x - row.center) / row.radius;
	return Math.abs(u) <= 1 ? Math.asin(u) : Math.sign(u) * (HALF_PI + Math.abs(u) - 1);
}

export function toX(rows: (y: number) => Row, longitude: number, y: number) {
	const row = rows(y);
	const reach =
		Math.abs(longitude) <= HALF_PI ? Math.sin(longitude) : Math.sign(longitude) * (1 + Math.abs(longitude) - HALF_PI);
	return row.center + row.radius * reach;
}

/** A curve drawn on the visible face, re-expressed as (longitude, y) so it can turn with the sphere. */
export function unproject(rows: (y: number) => Row, curve: Curve, offset = 0): Curve {
	const lift = (p: Point): Point => [toLongitude(rows, p[0], p[1]) + offset, p[1]];
	const fine = subdivide(curve);
	return { start: lift(fine.start), cubics: fine.cubics.map((c) => [lift(c[0]), lift(c[1]), lift(c[2])]) };
}

/** Far-side art is drawn on the unrolled surface already, so its x only needs rescaling to a longitude. */
export function fromSurface(curve: Curve, center: number, radius: number, offset = 0): Curve {
	const lift = (p: Point): Point => [(p[0] - center) / radius + offset, p[1]];
	const fine = subdivide(curve);
	return { start: lift(fine.start), cubics: fine.cubics.map((c) => [lift(c[0]), lift(c[1]), lift(c[2])]) };
}

/** Chains curves end to start, snapping each seam: near the limb a pixel of drift is degrees of longitude. */
export function chain(...curves: Curve[]): Curve {
	const cubics: Cubic[] = [];
	for (const curve of curves) {
		const last = cubics.at(-1);
		if (last) last[2] = curve.start;
		cubics.push(...curve.cubics.map((c): Cubic => [c[0], c[1], c[2]]));
	}
	return { start: curves[0]?.start ?? [0, 0], cubics };
}

function longitudeRange(curve: Curve): [west: number, east: number] {
	const all = [curve.start, ...curve.cubics.flat()].map((p) => p[0]);
	return [Math.min(...all), Math.max(...all)];
}

const format = (value: number) => (Math.round(value * 100) / 100).toString();

/** Three copies one turn apart keep the loop seamless for any angle in [0, 2π). */
export function projectPath(rows: (y: number) => Row, surface: Curve, angle: number) {
	const [west, east] = longitudeRange(surface);
	const parts: string[] = [];
	for (const turn of [-1, 0, 1]) {
		const shift = angle + turn * TAU;
		if (east + shift < -HALF_PI - 0.2 || west + shift > HALF_PI + 0.2) continue;
		const at = (p: Point) => `${format(toX(rows, p[0] + shift, p[1]))} ${format(p[1])}`;
		parts.push(`M${at(surface.start)}C${surface.cubics.map((c) => c.map(at).join(' ')).join(' ')}`);
	}
	return parts.join('') || 'M0 0';
}

export function wrapLongitude(longitude: number) {
	return ((((longitude + Math.PI) % TAU) + TAU) % TAU) - Math.PI;
}

/**
 * A flat mark centred on x = 0, pinned at `longitude` on row `y` and foreshortened like the surface under it;
 * `null` once it is behind. Full width within `flat` of the centre, so the drawn hearts keep their drawn size.
 */
export function pinTransform(rows: (y: number) => Row, longitude: number, y: number, angle: number, flat: number) {
	const turned = wrapLongitude(longitude + angle);
	const facing = Math.cos(turned);
	if (facing <= 0) return null;
	const scale = Math.min(1, facing / Math.cos(flat));
	return `translate(${format(toX(rows, turned, y))} 0) scale(${Math.round(scale * 1000) / 1000} 1)`;
}
